/**
 * Wall Art Calculator Engine
 *
 * Final Brain v1 implementation.
 * Pure calculation only: no DOM, no UI, no SVG.
 *
 * This engine does not own room presets or furniture defaults internally.
 * Those must be injected through createWallArtEngine(config).
 */

const ENGINE_EPSILON = 1e-9;
const ROOM_TYPES = ["living", "bedroom", "dining", "hallway", "bathroom", "kitchen"];
const FURNITURE_TYPE_KEYS = [
  "armchair",
  "sofa_2_seater",
  "sofa_3_seater",
  "sofa_custom",
  "sofa_large",
  "bed_single",
  "bed_small_double",
  "bed_double",
  "bed_king",
  "bed_super_king",
  "bed_custom",
  "sideboard",
  "console_table",
  "dining_table",
  "desk",
  "vanity_unit",
  "bath",
  "kitchen_worktop",
  "none"
];
const ROOM_FLAGS = ["seated_primary", "circulation_route", "wet_zone"];
const ENGINE_INPUT_LAYOUTS = ["single", "pair", "triptych"];
const ENGINE_INPUT_ORIENTATIONS = ["portrait", "landscape"];
const ENGINE_ISO_SIZES_ASC = ["A3", "A2", "A1", "A0"];
const ENGINE_ISO_SIZES_DESC = ["A0", "A1", "A2", "A3"];
const ALLOWED_INPUT_UNITS = ["cm", "in", "mm"];
const ALLOWED_ANCHOR_TYPES = ["sofa", "bed", "sideboard", "console", "desk", "vanity", "bath", "none"];

const FRAME_BORDER_CM = 2.5;
const BLANK_WALL_CENTRE_CM = 148;
const SEATED_PRIMARY_BLANK_WALL_CENTRE_CM = 140;
const FLOOR_BREATHING_ROOM_CM = 10;
const CEILING_BREATHING_ROOM_CM = 10;
const FURNITURE_CENTRED_VIABILITY_RATIO = 0.9;

const ISO_PRINT_SIZES_CM = {
  A3: {
    portrait: { widthCm: 29.7, heightCm: 42.0 },
    landscape: { widthCm: 42.0, heightCm: 29.7 }
  },
  A2: {
    portrait: { widthCm: 42.0, heightCm: 59.4 },
    landscape: { widthCm: 59.4, heightCm: 42.0 }
  },
  A1: {
    portrait: { widthCm: 59.4, heightCm: 84.1 },
    landscape: { widthCm: 84.1, heightCm: 59.4 }
  },
  A0: {
    portrait: { widthCm: 84.1, heightCm: 118.9 },
    landscape: { widthCm: 118.9, heightCm: 84.1 }
  }
};

const LAYOUT_DEFINITIONS = {
  single: { pieceCount: 1, simplicityFit: 1.0 },
  pair: { pieceCount: 2, simplicityFit: 0.92 },
  triptych: { pieceCount: 3, simplicityFit: 0.84 }
};

/**
 * Explicit spacing mapping for v1.
 *
 * Final Brain locks spacing classes but does not map them to size/layout families.
 * For implementation clarity, v1 uses:
 * - small = A3 / A2 multi-piece layouts = 4 cm
 * - medium = A1 multi-piece layouts = 5 cm
 * - large = A0 multi-piece layouts = 7 cm
 * - continuous triptych = 3 cm at every ISO size
 */
const LAYOUT_SPACING_CM = {
  single: { A3: 0, A2: 0, A1: 0, A0: 0 },
  pair: { A3: 4, A2: 4, A1: 5, A0: 7 },
  triptych: { A3: 3, A2: 3, A1: 3, A0: 3 }
};

const CLEARANCE_BANDS = {
  sofa: { ideal: 20, min: 15, max: 25 },
  bed: { ideal: 15, min: 10, max: 25 },
  sideboard: { ideal: 15, min: 10, max: 20 },
  console: { ideal: 15, min: 10, max: 20 },
  desk: { ideal: 15, min: 10, max: 20 },
  vanity: { ideal: 20, min: 15, max: 30 },
  bath: { ideal: 60, min: 45, max: 75 },
  none: null
};
const BED_WITH_HEADBOARD_CLEARANCE_BAND = { ideal: 18, min: 10, max: 25 };
const NO_HEADBOARD_BED_CENTRE_CM = 147;
const LOW_SIDEBOARD_OR_CONSOLE_THRESHOLD_CM = 76;
const LOW_SIDEBOARD_OR_CONSOLE_CENTRE_CM = 147;
const BATH_CENTRE_CM = 148;
const TALL_SIDEBOARD_OR_CONSOLE_NOTE_THRESHOLD_CM = 100;

const INPUT_TO_CM_MULTIPLIER = {
  cm: 1,
  in: 2.54,
  mm: 0.1
};

function runWallArtEngine() {
  throw new Error("runWallArtEngine requires configuration. Use createWallArtEngine(config).");
}

function createWallArtEngine(config) {
  const engineConfig = normalizeEngineConfig(config);

  return function runConfiguredWallArtEngine(input) {
    const resolution = resolveEngineInput(input, engineConfig);

    if (resolution.status === "error") {
      return resolution.result;
    }

    const resolvedInputs = resolution.resolvedInputs;
    const preferences = resolution.preferences;
    const assumptionsUsed = resolution.assumptionsUsed;
    const isEstimated = assumptionsUsed.some(function (entry) {
      return entry.code === "NO_HEADBOARD_MATTRESS_TOP_FALLBACK";
    });
    const warnings = buildBaseWarnings(assumptionsUsed);
    addRoomSafetyWarnings(warnings, resolvedInputs);
    const evaluation = evaluateScenario(resolvedInputs, preferences);

    if (!evaluation.winner) {
      return {
        status: "error",
        resolvedInputs: resolvedInputs,
        recommendation: null,
        previewGeometry: null,
        isEstimated: isEstimated,
        warnings: warnings,
        errors: [{ code: "NO_VIABLE_LAYOUT", meta: { roomType: resolvedInputs.roomType } }],
        assumptionsUsed: assumptionsUsed
      };
    }

    addOutcomeWarnings(warnings, evaluation, resolvedInputs, preferences);

    return {
      status: "ok",
      resolvedInputs: resolvedInputs,
      recommendation: buildRecommendation(evaluation),
      sizeOptions: buildSizeOptions(evaluation, resolvedInputs),
      previewGeometry: buildPreviewGeometry(evaluation, resolvedInputs),
      isEstimated: isEstimated,
      warnings: warnings,
      errors: [],
      assumptionsUsed: assumptionsUsed,
      debug: {
        winner: toCandidateDebug(evaluation.winner),
        scoredCandidates: evaluation.scoredCandidates.map(toCandidateDebug),
        rejectedCandidates: evaluation.rejectedCandidates.map(toRejectedCandidateDebug)
      }
    };
  };
}

function normalizeEngineConfig(config) {
  if (!config || typeof config !== "object") {
    throw new Error("createWallArtEngine requires a config object.");
  }

  const roomPresets = config.roomPresets;
  const furnitureDefaults = config.furnitureDefaults;

  if (!roomPresets || typeof roomPresets !== "object") {
    throw new Error("createWallArtEngine requires roomPresets.");
  }

  if (!furnitureDefaults || typeof furnitureDefaults !== "object") {
    throw new Error("createWallArtEngine requires furnitureDefaults.");
  }

  ROOM_TYPES.forEach(function (roomType) {
    if (!roomPresets[roomType]) {
      throw new Error("Missing room preset for " + roomType + ".");
    }

    validateRoomPreset(roomType, roomPresets[roomType]);
  });

  FURNITURE_TYPE_KEYS.forEach(function (furnitureTypeKey) {
    if (!furnitureDefaults[furnitureTypeKey]) {
      throw new Error("Missing furniture default for " + furnitureTypeKey + ".");
    }

    validateFurnitureDefault(furnitureTypeKey, furnitureDefaults[furnitureTypeKey]);
  });

  return {
    roomPresets: roomPresets,
    furnitureDefaults: furnitureDefaults
  };
}

function validateRoomPreset(roomType, preset) {
  if (!Array.isArray(preset.roomFlags)) {
    throw new Error("Room preset " + roomType + " must include roomFlags.");
  }

  preset.roomFlags.forEach(function (roomFlag) {
    if (!isAllowedValue(roomFlag, ROOM_FLAGS)) {
      throw new Error("Room preset " + roomType + " has invalid room flag " + roomFlag + ".");
    }
  });

  if (!isNullableFinitePositive(preset.defaultWallWidthCm)) {
    throw new Error("Room preset " + roomType + " has invalid defaultWallWidthCm.");
  }

  if (!isNullableFinitePositive(preset.defaultCeilingHeightCm)) {
    throw new Error("Room preset " + roomType + " has invalid defaultCeilingHeightCm.");
  }

  if (preset.allowWallCentredFallback != null && typeof preset.allowWallCentredFallback !== "boolean") {
    throw new Error("Room preset " + roomType + " has invalid allowWallCentredFallback.");
  }
}

function validateFurnitureDefault(furnitureTypeKey, furnitureDefault) {
  if (!isAllowedValue(furnitureDefault.anchorType, ALLOWED_ANCHOR_TYPES)) {
    throw new Error("Furniture default " + furnitureTypeKey + " has invalid anchorType.");
  }

  if (!isNullableFinitePositive(furnitureDefault.defaultWidthCm)) {
    throw new Error("Furniture default " + furnitureTypeKey + " has invalid defaultWidthCm.");
  }

  if (!isNullableFinitePositive(furnitureDefault.defaultTopHeightCm)) {
    throw new Error("Furniture default " + furnitureTypeKey + " has invalid defaultTopHeightCm.");
  }
}

function resolveEngineInput(input, engineConfig) {
  const invalidFields = [];
  const assumptionsUsed = [];

  if (!input || typeof input !== "object") {
    return {
      status: "error",
      result: invalidInputsResult(["input"])
    };
  }

  const inputUnit = isAllowedValue(input.inputUnit, ALLOWED_INPUT_UNITS)
    ? input.inputUnit
    : null;
  const roomType = isAllowedValue(input.roomType, ROOM_TYPES)
    ? input.roomType
    : null;
  const layoutPreference = isAllowedValue(input.preferences && input.preferences.layout, ["auto"].concat(ENGINE_INPUT_LAYOUTS))
    ? input.preferences.layout
    : null;
  const orientationPreference = isAllowedValue(input.preferences && input.preferences.orientation, ["auto"].concat(ENGINE_INPUT_ORIENTATIONS))
    ? input.preferences.orientation
    : null;

  if (!inputUnit) invalidFields.push("inputUnit");
  if (!roomType) invalidFields.push("roomType");
  if (!layoutPreference) invalidFields.push("preferences.layout");
  if (!orientationPreference) invalidFields.push("preferences.orientation");

  if (invalidFields.length) {
    return {
      status: "error",
      result: invalidInputsResult(invalidFields)
    };
  }

  const roomPreset = engineConfig.roomPresets[roomType];
  const wallWidthCm = resolveMeasurementWithPreset(
    input.wall && input.wall.width,
    inputUnit,
    roomPreset.defaultWallWidthCm,
    "wall.width",
    "ROOM_PRESET_WALL_WIDTH",
    assumptionsUsed,
    invalidFields
  );
  const ceilingHeightCm = resolveMeasurementWithPreset(
    input.wall && input.wall.ceilingHeight,
    inputUnit,
    roomPreset.defaultCeilingHeightCm,
    "wall.ceilingHeight",
    "ROOM_PRESET_CEILING_HEIGHT",
    assumptionsUsed,
    invalidFields
  );

  if (invalidFields.length) {
    return {
      status: "error",
      result: invalidInputsResult(invalidFields)
    };
  }

  const usableLimits = resolveUsableWallLimits(input.wall || {}, inputUnit, wallWidthCm, invalidFields);

  const hasAnchor = !!(input.anchor && input.anchor.hasAnchor && input.anchor.furnitureTypeKey && input.anchor.furnitureTypeKey !== "none");
  const furnitureTypeKey = hasAnchor && isAllowedValue(input.anchor.furnitureTypeKey, FURNITURE_TYPE_KEYS)
    ? input.anchor.furnitureTypeKey
    : "none";

  if (hasAnchor && furnitureTypeKey === "none") {
    invalidFields.push("anchor.furnitureTypeKey");
  }

  const anchorResolution = resolveAnchor(
    hasAnchor,
    furnitureTypeKey,
    input.anchor || {},
    inputUnit,
    wallWidthCm,
    engineConfig.furnitureDefaults,
    assumptionsUsed,
    invalidFields
  );

  if (invalidFields.length) {
    return {
      status: "error",
      result: invalidInputsResult(invalidFields)
    };
  }

  return {
    status: "ok",
    resolvedInputs: {
      roomType: roomType,
      roomFlags: roomPreset.roomFlags.slice(),
      allowWallCentredFallback: !!roomPreset.allowWallCentredFallback,
      wallWidthCm: wallWidthCm,
      usableWallWidthCm: usableLimits.widthCm,
      usableWallLeftLimitCm: usableLimits.leftCm,
      usableWallRightLimitCm: usableLimits.rightCm,
      ceilingHeightCm: ceilingHeightCm,
      furnitureTypeKey: anchorResolution.furnitureTypeKey,
      anchorType: anchorResolution.anchorType,
      anchorWidthCm: anchorResolution.widthCm,
      anchorTopHeightCm: anchorResolution.topHeightCm,
      anchorCentreXCm: anchorResolution.centreXCm,
      hasHeadboard: anchorResolution.hasHeadboard
    },
    preferences: {
      layout: layoutPreference,
      orientation: orientationPreference
    },
    assumptionsUsed: assumptionsUsed
  };
}

function resolveMeasurementWithPreset(inputValue, inputUnit, presetValueCm, field, assumptionCode, assumptionsUsed, invalidFields) {
  const suppliedValueCm = convertOptionalToCm(inputValue, inputUnit, field, invalidFields);
  if (suppliedValueCm != null) {
    if (!isFinitePositive(suppliedValueCm)) {
      invalidFields.push(field);
      return null;
    }
    return suppliedValueCm;
  }

  if (!isFinitePositive(presetValueCm)) {
    invalidFields.push(field);
    return null;
  }

  assumptionsUsed.push({
    code: assumptionCode,
    field: field,
    valueCm: presetValueCm
  });
  return presetValueCm;
}

function resolveUsableWallLimits(wallInput, inputUnit, wallWidthCm, invalidFields) {
  const leftProvided = wallInput.usableLeftLimit != null;
  const rightProvided = wallInput.usableRightLimit != null;

  if (leftProvided !== rightProvided) {
    invalidFields.push("wall.usableLeftLimit");
    invalidFields.push("wall.usableRightLimit");
    return { leftCm: 0, rightCm: wallWidthCm, widthCm: wallWidthCm };
  }

  if (!leftProvided && !rightProvided) {
    const fallbackMargin = Math.max(10, wallWidthCm * 0.05);
    return {
      leftCm: fallbackMargin,
      rightCm: wallWidthCm - fallbackMargin,
      widthCm: wallWidthCm - (fallbackMargin * 2)
    };
  }

  const leftCm = convertOptionalToCm(wallInput.usableLeftLimit, inputUnit, "wall.usableLeftLimit", invalidFields);
  const rightCm = convertOptionalToCm(wallInput.usableRightLimit, inputUnit, "wall.usableRightLimit", invalidFields);

  if (
    leftCm == null ||
    rightCm == null ||
    leftCm < 0 ||
    rightCm > wallWidthCm ||
    leftCm >= rightCm
  ) {
    invalidFields.push("wall.usableLeftLimit");
    invalidFields.push("wall.usableRightLimit");
    return { leftCm: 0, rightCm: wallWidthCm, widthCm: wallWidthCm };
  }

  return {
    leftCm: leftCm,
    rightCm: rightCm,
    widthCm: rightCm - leftCm
  };
}

function resolveAnchor(hasAnchor, furnitureTypeKey, anchorInput, inputUnit, wallWidthCm, furnitureDefaults, assumptionsUsed, invalidFields) {
  if (!hasAnchor) {
    return {
      furnitureTypeKey: "none",
      anchorType: "none",
      widthCm: null,
      topHeightCm: null,
      centreXCm: null,
      hasHeadboard: null
    };
  }

  const defaults = furnitureDefaults[furnitureTypeKey];
  if (!defaults) {
    invalidFields.push("anchor.furnitureTypeKey");
    return {
      furnitureTypeKey: "none",
      anchorType: "none",
      widthCm: null,
      topHeightCm: null,
      centreXCm: null,
      hasHeadboard: null
    };
  }

  const suppliedWidthCm = convertOptionalToCm(anchorInput.width, inputUnit, "anchor.width", invalidFields);
  let widthCm = suppliedWidthCm;
  if (widthCm == null) {
    widthCm = defaults.defaultWidthCm == null ? wallWidthCm : defaults.defaultWidthCm;
    assumptionsUsed.push({
      code: "FURNITURE_DEFAULT_WIDTH",
      field: "anchor.width",
      valueCm: widthCm
    });
  }

  const explicitHeadboard = defaults.anchorType === "bed"
    ? (anchorInput.hasHeadboard === false ? false : true)
    : null;

  const suppliedTopHeightCm = convertOptionalToCm(anchorInput.topHeight, inputUnit, "anchor.topHeight", invalidFields);
  let topHeightCm = suppliedTopHeightCm;

  if (topHeightCm == null) {
    if (defaults.anchorType === "bed" && explicitHeadboard === false) {
      topHeightCm = 60;
      assumptionsUsed.push({
        code: "NO_HEADBOARD_MATTRESS_TOP_FALLBACK",
        field: "anchor.topHeight",
        valueCm: topHeightCm
      });
    } else {
      topHeightCm = defaults.defaultTopHeightCm;
      assumptionsUsed.push({
        code: "FURNITURE_DEFAULT_TOP_HEIGHT",
        field: "anchor.topHeight",
        valueCm: topHeightCm
      });
    }
  }

  const suppliedCentreXCm = convertOptionalToCm(anchorInput.centreX, inputUnit, "anchor.centreX", invalidFields);
  let centreXCm = suppliedCentreXCm;
  if (centreXCm == null) {
    centreXCm = wallWidthCm / 2;
    assumptionsUsed.push({
      code: "DEFAULT_ANCHOR_CENTRE_X",
      field: "anchor.centreX",
      valueCm: centreXCm
    });
  }

  if (!isFinitePositive(widthCm)) invalidFields.push("anchor.width");
  if (!isFinitePositive(topHeightCm)) invalidFields.push("anchor.topHeight");
  if (!isFiniteNumber(centreXCm) || centreXCm < 0 || centreXCm > wallWidthCm) invalidFields.push("anchor.centreX");

  return {
    furnitureTypeKey: furnitureTypeKey,
    anchorType: defaults.anchorType,
    widthCm: widthCm,
    topHeightCm: topHeightCm,
    centreXCm: centreXCm,
    hasHeadboard: explicitHeadboard
  };
}

function evaluateScenario(resolvedInputs, preferences) {
  const widthTargets = buildWidthTargets(resolvedInputs);
  const placement = choosePlacementStrategy(resolvedInputs, widthTargets.minimumWidthCm);
  const clearanceBand = getClearanceBand(resolvedInputs);

  const context = {
    resolvedInputs: resolvedInputs,
    preferences: preferences,
    widthTargets: widthTargets,
    placement: placement,
    clearanceBand: clearanceBand,
    zoneAspect: computeZoneAspect(resolvedInputs, clearanceBand),
    blankWallCentreCm: resolvedInputs.roomFlags.indexOf("seated_primary") >= 0
      ? SEATED_PRIMARY_BLANK_WALL_CENTRE_CM
      : BLANK_WALL_CENTRE_CM
  };

  const scoredCandidates = [];
  const rejectedCandidates = [];
  const layouts = preferences.layout === "auto" ? ENGINE_INPUT_LAYOUTS : [preferences.layout];
  const orientations = preferences.orientation === "auto" ? ENGINE_INPUT_ORIENTATIONS : [preferences.orientation];

  layouts.forEach(function (layout) {
    orientations.forEach(function (orientation) {
      ENGINE_ISO_SIZES_ASC.forEach(function (isoSize) {
        const candidate = buildCandidate(layout, orientation, isoSize, context);
        if (candidate.accepted) {
          scoredCandidates.push(candidate);
        } else {
          rejectedCandidates.push(candidate);
        }
      });
    });
  });

  const rankedCandidates = scoredCandidates.slice().sort(function (a, b) {
    return compareCandidates(a, b, context.zoneAspect);
  });

  return {
    winner: rankedCandidates[0] || null,
    scoredCandidates: rankedCandidates,
    rejectedCandidates: rejectedCandidates,
    widthTargets: widthTargets,
    placement: placement,
    zoneAspect: context.zoneAspect,
    clearanceBand: clearanceBand
  };
}

function getClearanceBand(resolvedInputs) {
  if (resolvedInputs.anchorType === "none") {
    return null;
  }

  if (resolvedInputs.anchorType === "bed" && resolvedInputs.hasHeadboard !== false) {
    return BED_WITH_HEADBOARD_CLEARANCE_BAND;
  }

  return CLEARANCE_BANDS[resolvedInputs.anchorType];
}

function buildWidthTargets(resolvedInputs) {
  const sourceWidthCm = resolvedInputs.anchorType === "none"
    ? resolvedInputs.usableWallWidthCm
    : resolvedInputs.anchorWidthCm;
  const widthRatios = resolvedInputs.anchorType === "bath"
    ? { ideal: 0.60, minimum: 0.45, maximum: 0.75 }
    : { ideal: 0.67, minimum: 0.60, maximum: 0.75 };

  return {
    sourceWidthCm: sourceWidthCm,
    idealWidthCm: sourceWidthCm * widthRatios.ideal,
    minimumWidthCm: sourceWidthCm * widthRatios.minimum,
    maximumWidthCm: sourceWidthCm * widthRatios.maximum
  };
}

function choosePlacementStrategy(resolvedInputs, minimumWidthCm) {
  const usableCentreXCm = (resolvedInputs.usableWallLeftLimitCm + resolvedInputs.usableWallRightLimitCm) / 2;

  if (resolvedInputs.anchorType === "none") {
    return {
      mode: "wall",
      groupCentreXCm: usableCentreXCm,
      maxFurnitureCentredWidthCm: null,
      maxWallCentredWidthCm: resolvedInputs.usableWallWidthCm,
      widthLimited: false
    };
  }

  const maxFurnitureCentredWidthCm = Math.max(
    0,
    2 * Math.min(
      resolvedInputs.anchorCentreXCm - resolvedInputs.usableWallLeftLimitCm,
      resolvedInputs.usableWallRightLimitCm - resolvedInputs.anchorCentreXCm
    )
  );

  const maxWallCentredWidthCm = Math.max(
    0,
    2 * Math.min(
      usableCentreXCm - resolvedInputs.usableWallLeftLimitCm,
      resolvedInputs.usableWallRightLimitCm - usableCentreXCm
    )
  );

  const viableThresholdCm = minimumWidthCm * FURNITURE_CENTRED_VIABILITY_RATIO;
  const furnitureCentredViable = maxFurnitureCentredWidthCm >= viableThresholdCm;
  const wallCentredViable = maxWallCentredWidthCm >= viableThresholdCm;

  if (furnitureCentredViable) {
    return {
      mode: "anchor",
      groupCentreXCm: resolvedInputs.anchorCentreXCm,
      maxFurnitureCentredWidthCm: maxFurnitureCentredWidthCm,
      maxWallCentredWidthCm: maxWallCentredWidthCm,
      widthLimited: false
    };
  }

  if (resolvedInputs.allowWallCentredFallback && wallCentredViable) {
    return {
      mode: "wall",
      groupCentreXCm: usableCentreXCm,
      maxFurnitureCentredWidthCm: maxFurnitureCentredWidthCm,
      maxWallCentredWidthCm: maxWallCentredWidthCm,
      widthLimited: false
    };
  }

  return {
    mode: "anchor",
    groupCentreXCm: resolvedInputs.anchorCentreXCm,
    maxFurnitureCentredWidthCm: maxFurnitureCentredWidthCm,
    maxWallCentredWidthCm: maxWallCentredWidthCm,
    widthLimited: true
  };
}

function buildCandidate(layout, orientation, isoSize, context) {
  const piece = getOuterPieceSizeCm(isoSize, orientation);
  const spacingCm = getLayoutSpacingCm(layout, isoSize);
  const groupSize = getGroupSizeCm(layout, piece.widthCm, piece.heightCm, spacingCm);
  const placement = resolvePlacementCm(groupSize.heightCm, context);

  if (!placement.accepted) {
    return {
      accepted: false,
      reason: placement.reason,
      layout: layout,
      orientation: orientation,
      isoSize: isoSize,
      pieceCount: LAYOUT_DEFINITIONS[layout].pieceCount,
      pieceOuterWidthCm: piece.widthCm,
      pieceOuterHeightCm: piece.heightCm,
      totalArtworkWidthCm: groupSize.widthCm,
      totalArtworkHeightCm: groupSize.heightCm,
      spacingCm: spacingCm,
      groupCentreXCm: context.placement.groupCentreXCm,
      bottomFromFloorCm: placement.bottomFromFloorCm,
      centreFromFloorCm: placement.centreFromFloorCm,
      topFromFloorCm: placement.topFromFloorCm,
      clearanceAboveFurnitureCm: placement.clearanceAboveFurnitureCm
    };
  }

  const halfWidth = groupSize.widthCm / 2;
  const groupLeftCm = context.placement.groupCentreXCm - halfWidth;
  const groupRightCm = context.placement.groupCentreXCm + halfWidth;

  if (
    groupLeftCm < context.resolvedInputs.usableWallLeftLimitCm - ENGINE_EPSILON ||
    groupRightCm > context.resolvedInputs.usableWallRightLimitCm + ENGINE_EPSILON
  ) {
    return rejectedCandidateFromGeometry(
      "EXCEEDS_USABLE_WALL_WIDTH",
      layout,
      orientation,
      isoSize,
      piece,
      groupSize,
      context.placement.groupCentreXCm,
      placement
    );
  }

  if (
    (context.resolvedInputs.anchorType === "sofa" || context.resolvedInputs.anchorType === "bed") &&
    groupSize.widthCm > context.resolvedInputs.anchorWidthCm + ENGINE_EPSILON
  ) {
    return rejectedCandidateFromGeometry(
      "EXCEEDS_SOFA_OR_BED_WIDTH",
      layout,
      orientation,
      isoSize,
      piece,
      groupSize,
      context.placement.groupCentreXCm,
      placement
    );
  }

  const widthErrorCm = Math.abs(groupSize.widthCm - context.widthTargets.idealWidthCm);
  const widthFit = Math.max(0, 1 - (widthErrorCm / context.widthTargets.idealWidthCm));
  const simplicityFit = LAYOUT_DEFINITIONS[layout].simplicityFit;
  const finalScore = (0.80 * widthFit) + (0.20 * simplicityFit);
  const pieces = buildPieceGeometry(
    layout,
    piece.widthCm,
    piece.heightCm,
    spacingCm,
    context.placement.groupCentreXCm,
    placement.bottomFromFloorCm
  );

  return {
    accepted: true,
    layout: layout,
    orientation: orientation,
    isoSize: isoSize,
    pieceCount: LAYOUT_DEFINITIONS[layout].pieceCount,
    pieceOuterWidthCm: piece.widthCm,
    pieceOuterHeightCm: piece.heightCm,
    totalArtworkWidthCm: groupSize.widthCm,
    totalArtworkHeightCm: groupSize.heightCm,
    spacingCm: spacingCm,
    groupCentreXCm: context.placement.groupCentreXCm,
    bottomFromFloorCm: placement.bottomFromFloorCm,
    centreFromFloorCm: placement.centreFromFloorCm,
    topFromFloorCm: placement.topFromFloorCm,
    clearanceAboveFurnitureCm: placement.clearanceAboveFurnitureCm,
    headroomRemainingCm: placement.headroomRemainingCm,
    widthErrorCm: widthErrorCm,
    widthFit: widthFit,
    simplicityFit: simplicityFit,
    finalScore: finalScore,
    pieces: pieces
  };
}

function resolvePlacementCm(totalArtworkHeightCm, context) {
  const ceilingLimitCm = context.resolvedInputs.ceilingHeightCm - CEILING_BREATHING_ROOM_CM;

  if (context.resolvedInputs.anchorType === "none") {
    const centreFromFloorCm = context.blankWallCentreCm;
    const bottomFromFloorCm = centreFromFloorCm - (totalArtworkHeightCm / 2);
    const topFromFloorCm = centreFromFloorCm + (totalArtworkHeightCm / 2);
    const availableVerticalZoneCm = ceilingLimitCm - FLOOR_BREATHING_ROOM_CM;
    const headroomRemainingCm = availableVerticalZoneCm - totalArtworkHeightCm;

    if (
      bottomFromFloorCm < FLOOR_BREATHING_ROOM_CM - ENGINE_EPSILON ||
      topFromFloorCm > ceilingLimitCm + ENGINE_EPSILON
    ) {
      return {
        accepted: false,
        reason: "FAILS_CEILING_FIT",
        bottomFromFloorCm: bottomFromFloorCm,
        centreFromFloorCm: centreFromFloorCm,
        topFromFloorCm: topFromFloorCm,
        clearanceAboveFurnitureCm: null,
        headroomRemainingCm: headroomRemainingCm
      };
    }

    return {
      accepted: true,
      bottomFromFloorCm: bottomFromFloorCm,
      centreFromFloorCm: centreFromFloorCm,
      topFromFloorCm: topFromFloorCm,
      clearanceAboveFurnitureCm: null,
      headroomRemainingCm: headroomRemainingCm
    };
  }

  if (isDiningTableAnchor(context.resolvedInputs)) {
    const centreFromFloorCm = context.blankWallCentreCm;
    const bottomFromFloorCm = centreFromFloorCm - (totalArtworkHeightCm / 2);
    const topFromFloorCm = centreFromFloorCm + (totalArtworkHeightCm / 2);
    const availableVerticalZoneCm = ceilingLimitCm - FLOOR_BREATHING_ROOM_CM;
    const headroomRemainingCm = availableVerticalZoneCm - totalArtworkHeightCm;

    if (
      bottomFromFloorCm < FLOOR_BREATHING_ROOM_CM - ENGINE_EPSILON ||
      topFromFloorCm > ceilingLimitCm + ENGINE_EPSILON
    ) {
      return {
        accepted: false,
        reason: "FAILS_CEILING_FIT",
        bottomFromFloorCm: bottomFromFloorCm,
        centreFromFloorCm: centreFromFloorCm,
        topFromFloorCm: topFromFloorCm,
        clearanceAboveFurnitureCm: null,
        headroomRemainingCm: headroomRemainingCm
      };
    }

    return {
      accepted: true,
      bottomFromFloorCm: bottomFromFloorCm,
      centreFromFloorCm: centreFromFloorCm,
      topFromFloorCm: topFromFloorCm,
      clearanceAboveFurnitureCm: null,
      headroomRemainingCm: headroomRemainingCm
    };
  }

  if (isNoHeadboardBed(context.resolvedInputs)) {
    const centreFromFloorCm = NO_HEADBOARD_BED_CENTRE_CM;
    const bottomFromFloorCm = centreFromFloorCm - (totalArtworkHeightCm / 2);
    const topFromFloorCm = centreFromFloorCm + (totalArtworkHeightCm / 2);
    const clearanceAboveFurnitureCm = bottomFromFloorCm - context.resolvedInputs.anchorTopHeightCm;
    const headroomRemainingCm = ceilingLimitCm - topFromFloorCm;

    if (
      bottomFromFloorCm < context.resolvedInputs.anchorTopHeightCm - ENGINE_EPSILON ||
      bottomFromFloorCm < FLOOR_BREATHING_ROOM_CM - ENGINE_EPSILON ||
      topFromFloorCm > ceilingLimitCm + ENGINE_EPSILON
    ) {
      return {
        accepted: false,
        reason: "FAILS_CEILING_FIT",
        bottomFromFloorCm: bottomFromFloorCm,
        centreFromFloorCm: centreFromFloorCm,
        topFromFloorCm: topFromFloorCm,
        clearanceAboveFurnitureCm: clearanceAboveFurnitureCm,
        headroomRemainingCm: headroomRemainingCm
      };
    }

    return {
      accepted: true,
      bottomFromFloorCm: bottomFromFloorCm,
      centreFromFloorCm: centreFromFloorCm,
      topFromFloorCm: topFromFloorCm,
      clearanceAboveFurnitureCm: clearanceAboveFurnitureCm,
      headroomRemainingCm: headroomRemainingCm
    };
  }

  if (isLowSideboardOrConsole(context.resolvedInputs)) {
    const centreFromFloorCm = LOW_SIDEBOARD_OR_CONSOLE_CENTRE_CM;
    const bottomFromFloorCm = centreFromFloorCm - (totalArtworkHeightCm / 2);
    const topFromFloorCm = centreFromFloorCm + (totalArtworkHeightCm / 2);
    const clearanceAboveFurnitureCm = bottomFromFloorCm - context.resolvedInputs.anchorTopHeightCm;
    const headroomRemainingCm = ceilingLimitCm - topFromFloorCm;

    if (
      bottomFromFloorCm < context.resolvedInputs.anchorTopHeightCm - ENGINE_EPSILON ||
      bottomFromFloorCm < FLOOR_BREATHING_ROOM_CM - ENGINE_EPSILON ||
      topFromFloorCm > ceilingLimitCm + ENGINE_EPSILON
    ) {
      return {
        accepted: false,
        reason: "FAILS_CEILING_FIT",
        bottomFromFloorCm: bottomFromFloorCm,
        centreFromFloorCm: centreFromFloorCm,
        topFromFloorCm: topFromFloorCm,
        clearanceAboveFurnitureCm: clearanceAboveFurnitureCm,
        headroomRemainingCm: headroomRemainingCm
      };
    }

    return {
      accepted: true,
      bottomFromFloorCm: bottomFromFloorCm,
      centreFromFloorCm: centreFromFloorCm,
      topFromFloorCm: topFromFloorCm,
      clearanceAboveFurnitureCm: clearanceAboveFurnitureCm,
      headroomRemainingCm: headroomRemainingCm
    };
  }

  if (isBathAnchor(context.resolvedInputs)) {
    const centreFromFloorCm = BATH_CENTRE_CM;
    const bottomFromFloorCm = centreFromFloorCm - (totalArtworkHeightCm / 2);
    const topFromFloorCm = centreFromFloorCm + (totalArtworkHeightCm / 2);
    const clearanceAboveFurnitureCm = bottomFromFloorCm - context.resolvedInputs.anchorTopHeightCm;
    const headroomRemainingCm = ceilingLimitCm - topFromFloorCm;

    if (
      clearanceAboveFurnitureCm < context.clearanceBand.min - ENGINE_EPSILON ||
      bottomFromFloorCm < FLOOR_BREATHING_ROOM_CM - ENGINE_EPSILON ||
      topFromFloorCm > ceilingLimitCm + ENGINE_EPSILON
    ) {
      return {
        accepted: false,
        reason: "FAILS_CEILING_FIT",
        bottomFromFloorCm: bottomFromFloorCm,
        centreFromFloorCm: centreFromFloorCm,
        topFromFloorCm: topFromFloorCm,
        clearanceAboveFurnitureCm: clearanceAboveFurnitureCm,
        headroomRemainingCm: headroomRemainingCm
      };
    }

    return {
      accepted: true,
      bottomFromFloorCm: bottomFromFloorCm,
      centreFromFloorCm: centreFromFloorCm,
      topFromFloorCm: topFromFloorCm,
      clearanceAboveFurnitureCm: clearanceAboveFurnitureCm,
      headroomRemainingCm: headroomRemainingCm
    };
  }

  const minimumClearanceCm = context.clearanceBand.min;
  const idealClearanceCm = context.clearanceBand.ideal;
  const verticalFloorCm = context.resolvedInputs.anchorTopHeightCm + minimumClearanceCm;
  const availableVerticalZoneCm = ceilingLimitCm - verticalFloorCm;
  const headroomRemainingCm = availableVerticalZoneCm - totalArtworkHeightCm;

  if (totalArtworkHeightCm > availableVerticalZoneCm + ENGINE_EPSILON) {
    return {
      accepted: false,
      reason: "FAILS_CEILING_FIT",
      bottomFromFloorCm: verticalFloorCm,
      centreFromFloorCm: verticalFloorCm + (totalArtworkHeightCm / 2),
      topFromFloorCm: verticalFloorCm + totalArtworkHeightCm,
      clearanceAboveFurnitureCm: minimumClearanceCm,
      headroomRemainingCm: headroomRemainingCm
    };
  }

  const bottomFromFloorCm = context.resolvedInputs.anchorTopHeightCm + idealClearanceCm;
  const topFromFloorCm = bottomFromFloorCm + totalArtworkHeightCm;

  return {
    accepted: true,
    bottomFromFloorCm: bottomFromFloorCm,
    centreFromFloorCm: bottomFromFloorCm + (totalArtworkHeightCm / 2),
    topFromFloorCm: topFromFloorCm,
    clearanceAboveFurnitureCm: idealClearanceCm,
    headroomRemainingCm: headroomRemainingCm
  };
}

function isDiningTableAnchor(resolvedInputs) {
  // "desk" is kept as a legacy alias for older dining-table inputs.
  return resolvedInputs.roomType === "dining" && (
    resolvedInputs.furnitureTypeKey === "dining_table" ||
    resolvedInputs.furnitureTypeKey === "desk"
  );
}

function isBathAnchor(resolvedInputs) {
  return resolvedInputs.anchorType === "bath" || resolvedInputs.furnitureTypeKey === "bath";
}

function isNoHeadboardBed(resolvedInputs) {
  return resolvedInputs.anchorType === "bed" && resolvedInputs.hasHeadboard === false;
}

function isLowSideboardOrConsole(resolvedInputs) {
  return (
    (resolvedInputs.anchorType === "sideboard" || resolvedInputs.anchorType === "console") &&
    resolvedInputs.anchorTopHeightCm < LOW_SIDEBOARD_OR_CONSOLE_THRESHOLD_CM
  );
}

function isTallSideboardOrConsole(resolvedInputs) {
  return (
    (resolvedInputs.furnitureTypeKey === "sideboard" || resolvedInputs.furnitureTypeKey === "console_table") &&
    resolvedInputs.anchorTopHeightCm >= TALL_SIDEBOARD_OR_CONSOLE_NOTE_THRESHOLD_CM
  );
}

function getOuterPieceSizeCm(isoSize, orientation) {
  const printSize = ISO_PRINT_SIZES_CM[isoSize][orientation];
  return {
    widthCm: printSize.widthCm + (FRAME_BORDER_CM * 2),
    heightCm: printSize.heightCm + (FRAME_BORDER_CM * 2)
  };
}

function getLayoutSpacingCm(layout, isoSize) {
  return LAYOUT_SPACING_CM[layout][isoSize];
}

function getGroupSizeCm(layout, pieceWidthCm, pieceHeightCm, spacingCm) {
  if (layout === "single") {
    return { widthCm: pieceWidthCm, heightCm: pieceHeightCm };
  }

  if (layout === "pair") {
    return {
      widthCm: (pieceWidthCm * 2) + spacingCm,
      heightCm: pieceHeightCm
    };
  }

  if (layout === "triptych") {
    return {
      widthCm: (pieceWidthCm * 3) + (spacingCm * 2),
      heightCm: pieceHeightCm
    };
  }

  throw new Error("Unsupported layout: " + layout);
}

function buildPieceGeometry(layout, pieceWidthCm, pieceHeightCm, spacingCm, groupCentreXCm, groupBottomCm) {
  const pieces = [];

  if (layout === "single") {
    pieces.push(pieceGeometry(0, groupCentreXCm, pieceWidthCm, pieceHeightCm, groupBottomCm));
    return pieces;
  }

  if (layout === "pair") {
    pieces.push(pieceGeometry(0, groupCentreXCm - ((pieceWidthCm + spacingCm) / 2), pieceWidthCm, pieceHeightCm, groupBottomCm));
    pieces.push(pieceGeometry(1, groupCentreXCm + ((pieceWidthCm + spacingCm) / 2), pieceWidthCm, pieceHeightCm, groupBottomCm));
    return pieces;
  }

  if (layout === "triptych") {
    pieces.push(pieceGeometry(0, groupCentreXCm - (pieceWidthCm + spacingCm), pieceWidthCm, pieceHeightCm, groupBottomCm));
    pieces.push(pieceGeometry(1, groupCentreXCm, pieceWidthCm, pieceHeightCm, groupBottomCm));
    pieces.push(pieceGeometry(2, groupCentreXCm + (pieceWidthCm + spacingCm), pieceWidthCm, pieceHeightCm, groupBottomCm));
    return pieces;
  }

  throw new Error("Unsupported layout: " + layout);
}

function pieceGeometry(index, centreXCm, outerWidthCm, outerHeightCm, bottomFromFloorCm) {
  return {
    index: index,
    leftCm: centreXCm - (outerWidthCm / 2),
    rightCm: centreXCm + (outerWidthCm / 2),
    centreXCm: centreXCm,
    outerWidthCm: outerWidthCm,
    outerHeightCm: outerHeightCm,
    bottomFromFloorCm: bottomFromFloorCm,
    centreFromFloorCm: bottomFromFloorCm + (outerHeightCm / 2),
    topFromFloorCm: bottomFromFloorCm + outerHeightCm
  };
}

function compareCandidates(a, b, zoneAspect) {
  const scoreDiff = b.finalScore - a.finalScore;
  if (Math.abs(scoreDiff) >= 0.02) {
    return scoreDiff;
  }

  if (a.pieceCount !== b.pieceCount) {
    return a.pieceCount - b.pieceCount;
  }

  if (Math.abs(a.widthErrorCm - b.widthErrorCm) > ENGINE_EPSILON) {
    return a.widthErrorCm - b.widthErrorCm;
  }

  if (Math.abs(a.headroomRemainingCm - b.headroomRemainingCm) > ENGINE_EPSILON) {
    return b.headroomRemainingCm - a.headroomRemainingCm;
  }

  const orientationPreference = preferredOrientationForZone(zoneAspect);
  if (orientationPreference && a.orientation !== b.orientation) {
    if (a.orientation === orientationPreference) return -1;
    if (b.orientation === orientationPreference) return 1;
  }

  return isoSizeRank(b.isoSize) - isoSizeRank(a.isoSize);
}

function computeZoneAspect(resolvedInputs, clearanceBand) {
  const ceilingLimitCm = resolvedInputs.ceilingHeightCm - CEILING_BREATHING_ROOM_CM;
  const verticalFloorCm = resolvedInputs.anchorType === "none"
    ? FLOOR_BREATHING_ROOM_CM
    : resolvedInputs.anchorTopHeightCm + clearanceBand.min;
  const availableVerticalZoneCm = ceilingLimitCm - verticalFloorCm;
  return resolvedInputs.usableWallWidthCm / availableVerticalZoneCm;
}

function preferredOrientationForZone(zoneAspect) {
  if (zoneAspect >= 1.3) return "landscape";
  if (zoneAspect <= 0.8) return "portrait";
  return null;
}

function buildBaseWarnings(assumptionsUsed) {
  const warnings = [];

  if (assumptionsUsed.length > 0) {
    warnings.push({
      code: "DEFAULTS_USED",
      meta: {
        fields: uniqueStrings(assumptionsUsed.map(function (entry) {
          return entry.field;
        }))
      }
    });
  }

  if (assumptionsUsed.some(function (entry) { return entry.code === "NO_HEADBOARD_MATTRESS_TOP_FALLBACK"; })) {
    warnings.push({
      code: "NO_HEADBOARD_LOW_PLACEMENT"
    });
  }

  return warnings;
}

function addRoomSafetyWarnings(warnings, resolvedInputs) {
  if (resolvedInputs.roomType === "bathroom") {
    pushUniqueWarning(warnings, {
      code: "BATHROOM_PLACEMENT_CHECK",
      meta: {
        message: "Before hanging, avoid direct splash areas, mirrors, towel rails and powered fittings, and check the wall before drilling."
      }
    });

    if (isBathAnchor(resolvedInputs)) {
      pushUniqueWarning(warnings, {
        code: "BATH_PLACEMENT_CHECK",
        meta: {
          message: "Avoid direct splash zones, shower spray and heavy steam. Use suitable framing and ventilation for bathroom placement."
        }
      });
    }
  }

  if (resolvedInputs.roomType === "kitchen") {
    pushUniqueWarning(warnings, {
      code: "KITCHEN_PLACEMENT_CHECK",
      meta: {
        message: "Before hanging, avoid hobs, sinks, steam, grease, wall cupboards and sockets, and check the wall before drilling."
      }
    });
  }

  if (isTallSideboardOrConsole(resolvedInputs)) {
    pushUniqueWarning(warnings, { code: "TALL_FURNITURE_VIEWING_CHECK" });
  }
}

function addOutcomeWarnings(warnings, evaluation, resolvedInputs, preferences) {
  const winner = evaluation.winner;
  const targets = evaluation.widthTargets;

  if (winner.totalArtworkWidthCm < targets.minimumWidthCm - ENGINE_EPSILON) {
    pushUniqueWarning(warnings, { code: "UNDERSIZED" });
  }

  if (winner.totalArtworkWidthCm > targets.maximumWidthCm + ENGINE_EPSILON) {
    pushUniqueWarning(warnings, { code: "ABOVE_WIDTH_BAND" });
  }

  if (hasCeilingLimitedLargerOption(evaluation, resolvedInputs)) {
    pushUniqueWarning(warnings, { code: "LOW_CEILING_LIMITED_SIZE_OPTIONS" });
  }

  if (
    winner.clearanceAboveFurnitureCm != null &&
    evaluation.clearanceBand &&
    !isNoHeadboardBed(resolvedInputs) &&
    !isLowSideboardOrConsole(resolvedInputs) &&
    !isBathAnchor(resolvedInputs) &&
    winner.clearanceAboveFurnitureCm > evaluation.clearanceBand.max + ENGINE_EPSILON
  ) {
    pushUniqueWarning(warnings, { code: "CLEARANCE_TOO_HIGH" });
  }

  if (evaluation.placement.widthLimited) {
    pushUniqueWarning(warnings, {
      code: "FURNITURE_CENTRED_WIDTH_LIMITED",
      meta: {
        maxFurnitureCentredWidthCm: evaluation.placement.maxFurnitureCentredWidthCm
      }
    });
  }

  if (preferences.orientation === "auto") {
    const preferredOrientation = getPreferredOrientationDiagnostic(winner.layout, evaluation.zoneAspect, targets);
    if (preferredOrientation && winner.orientation !== preferredOrientation) {
      pushUniqueWarning(warnings, { code: "ORIENTATION_CHANGED_FOR_FIT" });
    }
  }

  if (preferences.layout === "auto" && resolvedInputs.anchorType !== "none") {
    const blankWallEvaluation = evaluateScenario(blankWallResolvedInputs(resolvedInputs), preferences);
    if (blankWallEvaluation.winner && blankWallEvaluation.winner.layout !== winner.layout) {
      pushUniqueWarning(warnings, { code: "LAYOUT_CHANGED_FOR_FIT" });
    }
  }
}

function hasCeilingLimitedLargerOption(evaluation, resolvedInputs) {
  const winner = evaluation.winner;

  return evaluation.rejectedCandidates.some(function (candidate) {
    return candidate.reason === "FAILS_CEILING_FIT" &&
      isLargerCandidate(candidate, winner) &&
      candidateFitsHorizontalLimits(candidate, evaluation, resolvedInputs);
  });
}

function isLargerCandidate(candidate, winner) {
  return candidate.totalArtworkWidthCm > winner.totalArtworkWidthCm + ENGINE_EPSILON ||
    candidate.totalArtworkHeightCm > winner.totalArtworkHeightCm + ENGINE_EPSILON;
}

function candidateFitsHorizontalLimits(candidate, evaluation, resolvedInputs) {
  const halfWidth = candidate.totalArtworkWidthCm / 2;
  const groupLeftCm = evaluation.placement.groupCentreXCm - halfWidth;
  const groupRightCm = evaluation.placement.groupCentreXCm + halfWidth;

  if (
    groupLeftCm < resolvedInputs.usableWallLeftLimitCm - ENGINE_EPSILON ||
    groupRightCm > resolvedInputs.usableWallRightLimitCm + ENGINE_EPSILON
  ) {
    return false;
  }

  if (
    (resolvedInputs.anchorType === "sofa" || resolvedInputs.anchorType === "bed") &&
    candidate.totalArtworkWidthCm > resolvedInputs.anchorWidthCm + ENGINE_EPSILON
  ) {
    return false;
  }

  return true;
}

function getPreferredOrientationDiagnostic(layout, zoneAspect, targets) {
  const preferredOrientation = preferredOrientationForZone(zoneAspect);
  if (!preferredOrientation) {
    return null;
  }

  const viableSize = ENGINE_ISO_SIZES_DESC.find(function (isoSize) {
    const piece = getOuterPieceSizeCm(isoSize, preferredOrientation);
    const spacingCm = getLayoutSpacingCm(layout, isoSize);
    const groupSize = getGroupSizeCm(layout, piece.widthCm, piece.heightCm, spacingCm);
    return groupSize.widthCm >= targets.minimumWidthCm - ENGINE_EPSILON &&
      groupSize.widthCm <= targets.maximumWidthCm + ENGINE_EPSILON;
  });

  return viableSize ? preferredOrientation : null;
}

function blankWallResolvedInputs(resolvedInputs) {
  return {
    roomType: resolvedInputs.roomType,
    roomFlags: resolvedInputs.roomFlags.slice(),
    allowWallCentredFallback: resolvedInputs.allowWallCentredFallback,
    wallWidthCm: resolvedInputs.wallWidthCm,
    usableWallWidthCm: resolvedInputs.usableWallWidthCm,
    usableWallLeftLimitCm: resolvedInputs.usableWallLeftLimitCm,
    usableWallRightLimitCm: resolvedInputs.usableWallRightLimitCm,
    ceilingHeightCm: resolvedInputs.ceilingHeightCm,
    furnitureTypeKey: "none",
    anchorType: "none",
    anchorWidthCm: null,
    anchorTopHeightCm: null,
    anchorCentreXCm: null,
    hasHeadboard: null
  };
}

function buildRecommendation(evaluation) {
  return buildRecommendationFromCandidate(evaluation, evaluation.winner);
}

function buildRecommendationFromCandidate(evaluation, candidate) {
  return {
    idealWidthCm: evaluation.widthTargets.idealWidthCm,
    minimumWidthCm: evaluation.widthTargets.minimumWidthCm,
    maximumWidthCm: evaluation.widthTargets.maximumWidthCm,
    layout: candidate.layout,
    orientation: candidate.orientation,
    isoSize: candidate.isoSize,
    pieceCount: candidate.pieceCount
  };
}

function buildSizeOptions(evaluation, resolvedInputs) {
  const winner = evaluation.winner;
  const optionTargetPercents = resolvedInputs.anchorType === "bath"
    ? { safe: 0.45, recommended: 0.60, statement: 0.75 }
    : { safe: 0.60, recommended: 0.67, statement: 0.75 };
  const safeCandidate = pickSizeOptionCandidate(
    evaluation.scoredCandidates,
    evaluation.widthTargets.minimumWidthCm,
    winner,
    evaluation
  );
  const statementCandidate = pickSizeOptionCandidate(
    evaluation.scoredCandidates,
    evaluation.widthTargets.maximumWidthCm,
    winner,
    evaluation
  );
  let safe = safeCandidate ? buildSizeOption("safe", "Safe", optionTargetPercents.safe, evaluation.widthTargets.minimumWidthCm, evaluation, resolvedInputs, safeCandidate) : null;
  let statement = statementCandidate ? buildSizeOption("statement", "Statement", optionTargetPercents.statement, evaluation.widthTargets.maximumWidthCm, evaluation, resolvedInputs, statementCandidate) : null;

  if (safe && statement && isSameCandidate(safeCandidate, statementCandidate)) {
    const safeDistanceCm = Math.abs(safeCandidate.totalArtworkWidthCm - evaluation.widthTargets.minimumWidthCm);
    const statementDistanceCm = Math.abs(statementCandidate.totalArtworkWidthCm - evaluation.widthTargets.maximumWidthCm);

    if (safeDistanceCm <= statementDistanceCm) {
      statement = null;
    } else {
      safe = null;
    }
  }

  return {
    safe: safe,
    recommended: buildSizeOption("recommended", "Recommended", optionTargetPercents.recommended, evaluation.widthTargets.idealWidthCm, evaluation, resolvedInputs, winner),
    statement: statement
  };
}

function pickSizeOptionCandidate(candidates, targetWidthCm, winner, evaluation) {
  return candidates
    .filter(function (candidate) {
      return !isSameCandidate(candidate, winner);
    })
    .slice()
    .sort(function (a, b) {
      const targetDiff = Math.abs(a.totalArtworkWidthCm - targetWidthCm) - Math.abs(b.totalArtworkWidthCm - targetWidthCm);
      if (Math.abs(targetDiff) > ENGINE_EPSILON) {
        return targetDiff;
      }

      return compareCandidates(a, b, evaluation.zoneAspect);
    })[0] || null;
}

function buildSizeOption(key, label, targetPercent, targetWidthCm, evaluation, resolvedInputs, candidate) {
  return {
    key: key,
    label: label,
    targetPercent: targetPercent,
    targetWidthCm: targetWidthCm,
    widthDifferenceCm: candidate.totalArtworkWidthCm - targetWidthCm,
    recommendation: buildRecommendationFromCandidate(evaluation, candidate),
    artwork: buildArtworkOptionFromCandidate(candidate),
    previewGeometry: buildPreviewGeometryFromCandidate(evaluation, resolvedInputs, candidate)
  };
}

function buildArtworkOptionFromCandidate(candidate) {
  return {
    pieceOuterWidthCm: candidate.pieceOuterWidthCm,
    pieceOuterHeightCm: candidate.pieceOuterHeightCm,
    pieceCount: candidate.pieceCount,
    totalArtworkWidthCm: candidate.totalArtworkWidthCm,
    totalArtworkHeightCm: candidate.totalArtworkHeightCm,
    spacingCm: candidate.spacingCm,
    bottomFromFloorCm: candidate.bottomFromFloorCm,
    centreFromFloorCm: candidate.centreFromFloorCm,
    topFromFloorCm: candidate.topFromFloorCm,
    clearanceAboveFurnitureCm: candidate.clearanceAboveFurnitureCm
  };
}

function isSameCandidate(a, b) {
  return !!a && !!b &&
    a.layout === b.layout &&
    a.orientation === b.orientation &&
    a.isoSize === b.isoSize;
}

function buildPreviewGeometry(evaluation, resolvedInputs) {
  return buildPreviewGeometryFromCandidate(evaluation, resolvedInputs, evaluation.winner);
}

function buildPreviewGeometryFromCandidate(evaluation, resolvedInputs, candidate) {
  return {
    wall: {
      wallWidthCm: resolvedInputs.wallWidthCm,
      usableWallWidthCm: resolvedInputs.usableWallWidthCm,
      usableWallLeftLimitCm: resolvedInputs.usableWallLeftLimitCm,
      usableWallRightLimitCm: resolvedInputs.usableWallRightLimitCm,
      ceilingHeightCm: resolvedInputs.ceilingHeightCm
    },
    anchor: {
      type: resolvedInputs.anchorType,
      widthCm: resolvedInputs.anchorWidthCm,
      topHeightCm: resolvedInputs.anchorTopHeightCm,
      centreXCm: resolvedInputs.anchorCentreXCm,
      hasHeadboard: resolvedInputs.hasHeadboard
    },
    artwork: {
      pieceOuterWidthCm: candidate.pieceOuterWidthCm,
      pieceOuterHeightCm: candidate.pieceOuterHeightCm,
      pieceCount: candidate.pieceCount,
      totalArtworkWidthCm: candidate.totalArtworkWidthCm,
      totalArtworkHeightCm: candidate.totalArtworkHeightCm,
      spacingCm: candidate.spacingCm,
      groupCentreXCm: candidate.groupCentreXCm,
      bottomFromFloorCm: candidate.bottomFromFloorCm,
      centreFromFloorCm: candidate.centreFromFloorCm,
      topFromFloorCm: candidate.topFromFloorCm,
      clearanceAboveFurnitureCm: candidate.clearanceAboveFurnitureCm,
      pieces: candidate.pieces.map(function (piece) {
        return Object.assign({}, piece);
      })
    }
  };
}

function rejectedCandidateFromGeometry(reason, layout, orientation, isoSize, piece, groupSize, groupCentreXCm, placement) {
  return {
    accepted: false,
    reason: reason,
    layout: layout,
    orientation: orientation,
    isoSize: isoSize,
    pieceCount: LAYOUT_DEFINITIONS[layout].pieceCount,
    pieceOuterWidthCm: piece.widthCm,
    pieceOuterHeightCm: piece.heightCm,
    totalArtworkWidthCm: groupSize.widthCm,
    totalArtworkHeightCm: groupSize.heightCm,
    spacingCm: getLayoutSpacingCm(layout, isoSize),
    groupCentreXCm: groupCentreXCm,
    bottomFromFloorCm: placement.bottomFromFloorCm,
    centreFromFloorCm: placement.centreFromFloorCm,
    topFromFloorCm: placement.topFromFloorCm,
    clearanceAboveFurnitureCm: placement.clearanceAboveFurnitureCm
  };
}

function toCandidateDebug(candidate) {
  return {
    layout: candidate.layout,
    orientation: candidate.orientation,
    isoSize: candidate.isoSize,
    pieceCount: candidate.pieceCount,
    pieceOuterWidthCm: candidate.pieceOuterWidthCm,
    pieceOuterHeightCm: candidate.pieceOuterHeightCm,
    totalArtworkWidthCm: candidate.totalArtworkWidthCm,
    totalArtworkHeightCm: candidate.totalArtworkHeightCm,
    spacingCm: candidate.spacingCm,
    groupCentreXCm: candidate.groupCentreXCm,
    bottomFromFloorCm: candidate.bottomFromFloorCm,
    centreFromFloorCm: candidate.centreFromFloorCm,
    topFromFloorCm: candidate.topFromFloorCm,
    clearanceAboveFurnitureCm: candidate.clearanceAboveFurnitureCm,
    headroomRemainingCm: candidate.headroomRemainingCm,
    widthErrorCm: candidate.widthErrorCm,
    widthFit: candidate.widthFit,
    simplicityFit: candidate.simplicityFit,
    finalScore: candidate.finalScore
  };
}

function toRejectedCandidateDebug(candidate) {
  return {
    reason: candidate.reason,
    layout: candidate.layout,
    orientation: candidate.orientation,
    isoSize: candidate.isoSize,
    pieceCount: candidate.pieceCount,
    pieceOuterWidthCm: candidate.pieceOuterWidthCm,
    pieceOuterHeightCm: candidate.pieceOuterHeightCm,
    totalArtworkWidthCm: candidate.totalArtworkWidthCm,
    totalArtworkHeightCm: candidate.totalArtworkHeightCm,
    spacingCm: candidate.spacingCm,
    groupCentreXCm: candidate.groupCentreXCm,
    bottomFromFloorCm: candidate.bottomFromFloorCm,
    centreFromFloorCm: candidate.centreFromFloorCm,
    topFromFloorCm: candidate.topFromFloorCm,
    clearanceAboveFurnitureCm: candidate.clearanceAboveFurnitureCm
  };
}

function invalidInputsResult(fields) {
  return {
    status: "error",
    resolvedInputs: null,
    recommendation: null,
    previewGeometry: null,
    isEstimated: false,
    warnings: [],
    errors: [{
      code: "INVALID_INPUTS",
      meta: { fields: uniqueStrings(fields) }
    }],
    assumptionsUsed: []
  };
}

function pushUniqueWarning(warnings, warning) {
  if (!warnings.some(function (entry) { return entry.code === warning.code; })) {
    warnings.push(warning);
  }
}

function convertOptionalToCm(value, unit, field, invalidFields) {
  if (value == null || value === "") {
    return null;
  }

  const numeric = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(numeric)) {
    invalidFields.push(field);
    return null;
  }

  return numeric * INPUT_TO_CM_MULTIPLIER[unit];
}

function isAllowedValue(value, allowedValues) {
  return allowedValues.indexOf(value) >= 0;
}

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function isFinitePositive(value) {
  return isFiniteNumber(value) && value > 0;
}

function isNullableFinitePositive(value) {
  return value == null || isFinitePositive(value);
}

function isoSizeRank(isoSize) {
  return ENGINE_ISO_SIZES_ASC.indexOf(isoSize);
}

function uniqueStrings(values) {
  return values.filter(function (value, index) {
    return values.indexOf(value) === index;
  });
}
